import React, { memo, useMemo } from 'react';
import {
  View,
  Text,
  Image,
  Pressable,
  StyleSheet,
  StyleProp,
  ViewStyle,
} from 'react-native';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { MarketplaceListing, getConditionDisplayText } from '@/types/residents/marketplaceListing';
import { useHover } from '@/hooks/common/useHover';
import { WEB_STYLES } from '@/utils/webStyles';

interface MarketplaceListingCardProps {
  listing: MarketplaceListing;
  onPress: () => void;
  style?: StyleProp<ViewStyle>;
}

const DETAILS_HEIGHT = 84.5;
const PropertyPlaceholder = RESIDENTS_SVG_COMPONENTS.PROPERTY_PLACEHOLDER;

export function formatTimeAgo(dateString: string): string {
  const now = Date.now();
  const then = new Date(dateString).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;

  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths}mo ago`;
}

const HOVERED_BORDER = 'rgba(255,255,255,0.18)';

export const MarketplaceListingCard: React.FC<MarketplaceListingCardProps> = memo(({
  listing,
  onPress,
  style,
}) => {
  const { colors } = useThemeColors();
  const firstImageUrl = listing.images?.[0]?.url;
  const conditionText = getConditionDisplayText(listing.condition);
  const timeAgo = useMemo(() => formatTimeAgo(listing.createdAt), [listing.createdAt]);
  const { isHovered, hoverProps } = useHover();

  return (
    <Pressable
      style={[
        styles.container,
        { backgroundColor: colors.card.bg, borderColor: colors.card.border },
        webCardStyle,
        isHovered && styles.containerHovered,
        style,
      ]}
      onPress={onPress}
      {...hoverProps}
    >
      <View style={styles.imageArea}>
        {firstImageUrl ? (
          <Image
            source={{ uri: firstImageUrl }}
            style={styles.image}
            resizeMode="cover"
          />
        ) : (
          <View style={[styles.placeholder, { backgroundColor: colors.card.bg }]}>
            <PropertyPlaceholder width={80} height={80} />
          </View>
        )}
      </View>

      <View style={styles.details}>
        <Text style={[styles.title, { color: colors.text.primary }]} numberOfLines={1}>
          {listing.title}
        </Text>
        <Text style={[styles.price, { color: colors.text.muted }]} numberOfLines={1}>
          {listing.price}
        </Text>
        <View style={styles.metaRow}>
          <View style={[styles.conditionBadge, { backgroundColor: colors.accent.greenLight }]}>
            <Text style={[styles.conditionText, { color: colors.accent.green }]}>{conditionText}</Text>
          </View>
          <Text style={[styles.timeAgo, { color: colors.text.dimmer }]}>{timeAgo}</Text>
        </View>
      </View>
    </Pressable>
  );
});

MarketplaceListingCard.displayName = 'MarketplaceListingCard';

const webCardStyle = {
  ...WEB_STYLES.transition('all', '0.2s', 'ease'),
} as any;

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderRadius: 16,
    overflow: 'hidden',
    padding: 1,
  },
  containerHovered: {
    borderColor: HOVERED_BORDER,
    transform: [{ scale: 1.02 }],
  },
  imageArea: {
    flex: 1,
    minHeight: 120,
  },
  image: {
    width: '100%',
    height: '100%',
  },
  placeholder: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  details: {
    height: DETAILS_HEIGHT,
    paddingHorizontal: 14,
    paddingTop: 12,
  },
  price: {
    ...REDESIGN_TYPOGRAPHY.listingTitle,
    marginTop: 4,
  },
  title: {
    ...REDESIGN_TYPOGRAPHY.listingPrice,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 7.5,
  },
  conditionBadge: {
    borderRadius: 4,
    paddingHorizontal: 7,
    paddingVertical: 2,
  },
  conditionText: {
    ...REDESIGN_TYPOGRAPHY.conditionBadge,
  },
  timeAgo: {
    ...REDESIGN_TYPOGRAPHY.timestamp,
  },
});
